import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-6" data-testid="reset-password-page">
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20"><Logo imgClassName="h-7" /></div>
      <div className="w-full max-w-sm">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Set a new password</h1>
        <p className="text-muted-foreground mt-2 text-sm">Choose a strong password you don't use elsewhere.</p>
        {!token ? (
          <p className="text-sm text-destructive mt-6" data-testid="reset-no-token">This reset link is invalid. Please request a new one from the <Link to="/forgot-password" className="underline">forgot password</Link> page.</p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-wider">New password</Label>
              <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="reset-password-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cpw" className="text-xs font-semibold uppercase tracking-wider">Confirm password</Label>
              <Input id="cpw" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="reset-confirm-input" className="h-11" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full h-11 active:scale-[0.98] transition-transform">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
        <p className="text-sm text-muted-foreground mt-8 text-center">
          <Link to="/login" className="text-accent font-medium hover:underline" data-testid="reset-goto-login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
